import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiGet, apiPost, apiRequest } from '../../utils/api'
import type {
  Zone,
  CreateZoneRequest,
  UpdateZoneRequest,
  ZoneSliceState,
} from '../../features/zones/types'

const initialState: ZoneSliceState = {
  zones: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  pageSize: 1000,
  stats: {
    total: 0,
    low: 0,
    medium: 0,
    high: 0,
  },
  globalStats: null,
}

// Helper function to compute stats from zones array
const computeStats = (items: Zone[]) => {
  const low = items.filter(z => (z.score ?? 0) < 3).length
  const medium = items.filter(
    z => (z.score ?? 0) >= 3 && (z.score ?? 0) < 5
  ).length
  const high = items.filter(z => (z.score ?? 0) >= 5).length
  return {
    total: items.length,
    low,
    medium,
    high,
  }
}

/**
 * Backend zone type for this slice
 * Note: This is a local type definition. For shared types, use features/zones/types
 */
type BackendZone = {
  id: number | string
  name: string
  polygon?: {
    type: 'Polygon'
    coordinates: [number, number][][]
  }
  area?: number
  score?: number
  notes?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: number
  userId?: number
  creator?: {
    id: number
    name: string
    email: string
  }
  updater?: {
    id: number
    name: string
    email: string
  }
}

/**
 * Converts backend zone format to frontend zone format
 */
const convertBackendZoneToFrontend = (backendZone: BackendZone): Zone => {
  // Backend polygon is GeoJSON format: { type: 'Polygon', coordinates: [[[lng, lat], ...]] }
  // Frontend expects: [[lat, lng], ...]
  let coordinates: number[][] = []
  if (
    backendZone.polygon?.coordinates &&
    Array.isArray(backendZone.polygon.coordinates[0])
  ) {
    // Convert from GeoJSON format [[lng, lat], ...] to frontend format [[lat, lng], ...]
    coordinates = backendZone.polygon.coordinates[0].map(
      (coord: [number, number]) => [coord[1], coord[0]]
    )
  }

  // Use area from backend only - backend calculates and stores area in database
  const area = backendZone.area ?? 0

  const zone: Zone = {
    id: String(backendZone.id),
    name: backendZone.name,
    coordinates,
    score: backendZone.score ?? 0,
    area: area,
    notes: backendZone.notes ?? '',
    ...(backendZone.createdAt ? { createdAt: backendZone.createdAt } : {}),
    ...(backendZone.updatedAt ? { updatedAt: backendZone.updatedAt } : {}),
    ...((backendZone.createdBy ?? backendZone.userId)
      ? { userId: backendZone.createdBy ?? backendZone.userId }
      : {}),
  }

  if (backendZone.creator) {
    zone.creator = {
      id: backendZone.creator.id,
      name: backendZone.creator.name,
      email: backendZone.creator.email,
    }
  }

  if (backendZone.updater) {
    zone.updater = {
      id: backendZone.updater.id,
      name: backendZone.updater.name,
      email: backendZone.updater.email,
    }
  }

  return zone
}

/**
 * Helper to convert coordinates to GeoJSON Polygon
 */
const convertCoordinatesToPolygon = (coordinates: number[][] | undefined) => {
  if (!coordinates || coordinates.length === 0) return undefined

  // Ensure polygon is closed
  const coords = [...coordinates]
  const first = coords[0]
  const last = coords[coords.length - 1]
  if (
    first &&
    first.length >= 2 &&
    last &&
    last.length >= 2 &&
    (first[0] !== last[0] || first[1] !== last[1])
  ) {
    coords.push([first[0]!, first[1]!])
  }

  // Convert to GeoJSON format: [[lng, lat], ...]
  const geoJsonCoords = coords
    .filter(coord => coord && coord.length >= 2)
    .map(coord => [coord[1]!, coord[0]!] as [number, number])

  if (geoJsonCoords.length === 0) return undefined

  return {
    type: 'Polygon' as const,
    coordinates: [geoJsonCoords],
  }
}

/**
 * Converts frontend zone format to backend API format
 */
const convertFrontendZoneToBackend = (
  zone: Partial<Zone>
): Partial<CreateZoneRequest> | Partial<UpdateZoneRequest> => {
  const result: Partial<CreateZoneRequest> & Partial<UpdateZoneRequest> = {}

  if (zone.name !== undefined) result.name = zone.name

  if (zone.score !== undefined) {
    // Ensure score is within valid range (0-6)
    result.score = Math.min(6, Math.max(0, Math.round(zone.score)))
  }

  const polygon = convertCoordinatesToPolygon(zone.coordinates)
  if (polygon) {
    result.polygon = polygon
  }

  // Always include notes if provided (even if empty string, to allow clearing notes)
  if (zone.notes !== undefined && zone.notes !== null) {
    result.notes = zone.notes.trim() === '' ? null : zone.notes.trim()
  }

  return result
}
// Helper to prepare zone data
const prepareZoneForBackend = (zone: {
  name: string
  score: number
  coordinates: number[][]
  notes?: string | null
}) => {
  const backendData = convertFrontendZoneToBackend({
    name: zone.name,
    score: zone.score,
    coordinates: zone.coordinates,
    notes: zone.notes ?? null,
  })

  if (!backendData.name || !backendData.polygon) {
    throw new Error(`Invalid zone data for: ${zone.name}`)
  }

  return {
    name: backendData.name,
    score: Math.min(6, Math.max(0, Math.round(backendData.score ?? 0))),
    polygon: backendData.polygon,
    ...(backendData.notes !== undefined && { notes: backendData.notes }),
  }
}
// Fetch zones params (specific to this slice)
interface FetchZonesSliceParams {
  page?: number
  pageSize?: number
  search?: string
  riskFilter?: 'all' | 'low' | 'medium' | 'high'
}

/**
 * Helper to build fetch zones query parameters
 */
const buildFetchZonesQuery = (params: FetchZonesSliceParams | undefined) => {
  const page = params?.page && params.page > 0 ? params.page : 1
  const pageSize =
    params?.pageSize && params.pageSize > 0 ? params.pageSize : 1000

  const query = new URLSearchParams()
  query.append('page', String(page))
  query.append('pageSize', String(pageSize))

  if (params?.search && params.search.trim()) {
    query.append('search', params.search.trim())
  }

  // Map riskFilter to scoreMin/scoreMax for backend
  if (params?.riskFilter && params.riskFilter !== 'all') {
    if (params.riskFilter === 'low') {
      query.append('scoreMax', '2')
    } else if (params.riskFilter === 'medium') {
      query.append('scoreMin', '3')
      query.append('scoreMax', '4')
    } else if (params.riskFilter === 'high') {
      query.append('scoreMin', '5')
    }
  }

  return query
}

/**
 * Extract error message from response
 */
const getErrorMessage = async (response: Response, defaultMsg: string) => {
  const errorData = await response.json().catch(() => ({ message: defaultMsg }))

  if (errorData.errors && Array.isArray(errorData.errors)) {
    return (
      errorData.errors
        .map(
          (err: { msg?: string; message?: string }) => err.msg ?? err.message
        )
        .join(', ') ||
      errorData.message ||
      defaultMsg
    )
  }
  return errorData.message || defaultMsg
}

/**
 * Fetch zones from the backend API with filters and pagination
 */
export const fetchZones = createAsyncThunk(
  'zones/fetchZones',
  async (params: FetchZonesSliceParams | undefined, { rejectWithValue }) => {
    try {
      const query = buildFetchZonesQuery(params)
      const response = await apiGet(`/zones?${query.toString()}`)

      if (!response.ok) {
        return rejectWithValue(
          await getErrorMessage(response, 'Failed to fetch zones')
        )
      }

      const result = await response.json()
      if (result.success && result.data) {
        const paginated = result.data
        return {
          zones: (paginated.items || []).map(convertBackendZoneToFrontend),
          total: paginated.total || 0,
          page: paginated.page || (params?.page ?? 1),
          pageSize: paginated.pageSize || (params?.pageSize ?? 1000),
          stats: paginated.stats ?? undefined,
        }
      }
      return {
        zones: [] as Zone[],
        total: 0,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 1000,
        stats: undefined,
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch zones'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Helper to validate zone creation
 */
const validateZoneForCreation = (
  backendDataPartial: Partial<CreateZoneRequest>
) => {
  if (!backendDataPartial.name || backendDataPartial.name.trim() === '') {
    return 'Zone name is required'
  }
  if (!backendDataPartial.polygon) {
    return 'Zone coordinates are required'
  }
  return null
}

/**
 * Add a new zone to the backend database
 */
export const addZone = createAsyncThunk(
  'zones/addZone',
  async (zone: Partial<Zone>, { rejectWithValue }) => {
    try {
      const backendDataPartial = convertFrontendZoneToBackend(zone)

      const validationError = validateZoneForCreation(backendDataPartial)
      if (validationError) return rejectWithValue(validationError)

      if (backendDataPartial.score === undefined) {
        backendDataPartial.score = 0
      }
      // Ensure score is within valid range (0-6)
      const finalScore = Math.min(
        6,
        Math.max(0, Math.round(backendDataPartial.score))
      )

      const backendData: CreateZoneRequest = {
        name: backendDataPartial.name!,
        score: finalScore,
        polygon: backendDataPartial.polygon!,
        ...(backendDataPartial.notes !== undefined && {
          notes: backendDataPartial.notes,
        }),
      }

      const response = await apiPost('/zones', backendData)

      if (!response.ok) {
        return rejectWithValue(
          await getErrorMessage(response, 'Failed to create zone')
        )
      }

      const result = await response.json()
      if (result.success && result.data) {
        return convertBackendZoneToFrontend(result.data)
      }
      return rejectWithValue('Invalid response from server')
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create zone'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Batch create zones from grid cells
 */
export const createZonesBatch = createAsyncThunk(
  'zones/createZonesBatch',
  async (
    zones: Array<{
      name: string
      score: number
      coordinates: number[][]
      notes?: string | null
    }>,
    { rejectWithValue }
  ) => {
    try {
      // Validate batch size
      if (zones.length === 0) {
        return rejectWithValue('At least one zone is required')
      }
      if (zones.length > 100) {
        return rejectWithValue('Maximum 100 zones can be created at once')
      }

      // Convert zones to backend format
      const backendZones = zones.map(prepareZoneForBackend)

      const response = await apiPost('/zones/batch', { zones: backendZones })

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: 'Failed to create zones' }))
        return rejectWithValue(errorData.message || 'Failed to create zones')
      }

      const result = await response.json()
      if (result.success && result.data) {
        return {
          successful:
            result.data.successful?.map(convertBackendZoneToFrontend) || [],
          failed: result.data.failed || [],
        }
      }
      return rejectWithValue('Invalid response from server')
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create zones'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Update an existing zone in the backend database
 */
export const updateZone = createAsyncThunk(
  'zones/updateZone',
  async (
    { id, updates }: { id: string; updates: Partial<Zone> },
    { rejectWithValue }
  ) => {
    try {
      const backendData = convertFrontendZoneToBackend(
        updates
      ) as UpdateZoneRequest

      const response = await apiRequest(`/zones/${id}`, {
        method: 'PUT',
        body: JSON.stringify(backendData),
      })

      if (!response.ok) {
        return rejectWithValue(
          await getErrorMessage(response, 'Failed to update zone')
        )
      }

      const result = await response.json()
      if (result.success && result.data) {
        return convertBackendZoneToFrontend(result.data)
      }
      return rejectWithValue('Invalid response from server')
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update zone'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Delete a zone from the backend database
 */
export const deleteZone = createAsyncThunk(
  'zones/deleteZone',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/zones/${id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: 'Failed to delete zone' }))
        return rejectWithValue(errorData.message || 'Failed to delete zone')
      }

      return id
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete zone'
      return rejectWithValue(errorMessage)
    }
  }
)

const zoneSlice = createSlice({
  name: 'zones',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    // Local update without API call - for temporary UI updates
    updateZoneLocal: (
      state,
      action: { payload: { id: string; updates: Partial<Zone> } }
    ) => {
      const { id, updates } = action.payload
      const index = state.zones.findIndex(zone => zone.id === id)
      if (index !== -1) {
        state.zones[index] = { ...state.zones[index]!, ...updates }
      }
    },
  },
  extraReducers: builder => {
    // Fetch zones
    builder
      .addCase(fetchZones.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.loading = false
        state.zones = action.payload.zones
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize

        const { riskFilter, search } = action.meta.arg || {}
        const isUnfiltered =
          (!riskFilter || riskFilter === 'all') && (!search || search === '')

        // If unfiltered fetch, capture global stats
        if (isUnfiltered) {
          if (action.payload.stats) {
            state.globalStats = action.payload.stats
            state.stats = action.payload.stats
          } else {
            const derived = computeStats(state.zones)
            state.globalStats = derived
            state.stats = derived
          }
        } else if (state.globalStats) {
          // Filtered fetch: preserve globalStats, use it for stats
          state.stats = state.globalStats
        } else if (action.payload.stats) {
          // If no globalStats yet, use backend stats as fallback
          state.globalStats = action.payload.stats
          state.stats = action.payload.stats
        } else {
          const derived = computeStats(state.zones)
          state.stats = derived
        }
        state.error = null
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.zones = []
      })

    // Add zone
    builder
      .addCase(addZone.pending, state => {
        state.error = null
      })
      .addCase(addZone.fulfilled, (state, action) => {
        state.zones.push(action.payload)
        state.error = null
        // Update stats in real-time
        const stats = computeStats(state.zones)
        state.stats = stats
        state.globalStats = stats
        state.total = state.zones.length
      })
      .addCase(addZone.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Batch create zones
    builder
      .addCase(createZonesBatch.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createZonesBatch.fulfilled, (state, action) => {
        state.loading = false
        // Add successfully created zones to the list
        state.zones.push(...action.payload.successful)
        state.error = null
        // Log failed zones if any
        if (action.payload.failed.length > 0) {
          // eslint-disable-next-line no-console
          console.warn('Some zones failed to create:', action.payload.failed)
        }
        // Update stats in real-time
        const stats = computeStats(state.zones)
        state.stats = stats
        state.globalStats = stats
        state.total = state.zones.length
      })
      .addCase(createZonesBatch.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update zone
    builder
      .addCase(updateZone.pending, state => {
        state.error = null
      })
      .addCase(updateZone.fulfilled, (state, action) => {
        const index = state.zones.findIndex(
          zone => zone.id === action.payload.id
        )
        if (index !== -1) {
          state.zones[index] = action.payload
        }
        state.error = null
        // Update stats in real-time
        const stats = computeStats(state.zones)
        state.stats = stats
        state.globalStats = stats
        state.total = state.zones.length
      })
      .addCase(updateZone.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Delete zone
    builder
      .addCase(deleteZone.pending, state => {
        state.error = null
      })
      .addCase(deleteZone.fulfilled, (state, action) => {
        state.zones = state.zones.filter(zone => zone.id !== action.payload)
        state.error = null
        // Update stats in real-time
        const stats = computeStats(state.zones)
        state.stats = stats
        state.globalStats = stats
        state.total = state.zones.length
      })
      .addCase(deleteZone.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const { clearError, updateZoneLocal } = zoneSlice.actions
export default zoneSlice.reducer
